/**
 * Imperative styled confirmation - the SaaS replacement for window.confirm.
 *
 *   if (!(await confirmAction({
 *     title: 'Stop this enrollment?',
 *     description: "They'll get no more steps from this sequence.",
 *     confirmLabel: 'Stop enrollment',
 *     destructive: true,
 *   }))) return;
 *
 * Resolves true/false. For irreversible DELETES use confirmDelete() instead
 * (it makes the user type "delete"); this one is a single-click confirm for
 * reversible or medium-stakes actions.
 *
 * Pass `requireTyped` for an action that is reversible but consequential while
 * it lasts - switching conversions off, say. The button stays disabled until
 * the word is typed:
 *
 *   await confirmAction({ ..., requireTyped: 'OFF' })
 *
 * The point is not friction for its own sake. A single-click dialog on a
 * control someone reaches for often becomes muscle memory within a week, and
 * then it is not a confirmation, it is an extra click. Typing a word cannot be
 * done without reading.
 */
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export function confirmAction(opts = {}) {
  const {
    title = 'Are you sure?',
    description = '',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    requireTyped = null,
  } = opts;

  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    let done = false;
    const settle = (val) => {
      if (done) return;
      done = true;
      resolve(val);
      // Let the close animation finish before tearing down.
      setTimeout(() => { try { root.unmount(); } catch (_) { /* noop */ } host.remove(); }, 250);
    };

    function Wrapper() {
      const [open, setOpen] = useState(true);
      const [typed, setTyped] = useState('');
      const ready = !requireTyped || typed.trim().toLowerCase() === String(requireTyped).toLowerCase();
      const go = () => { if (!ready) return; setOpen(false); settle(true); };
      return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); settle(false); } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              {description ? <DialogDescription>{description}</DialogDescription> : null}
            </DialogHeader>
            {requireTyped ? (
              <div>
                <label className="text-[13px] text-muted-foreground" htmlFor="confirm-typed">
                  Type <b className="font-semibold text-foreground">{requireTyped}</b> to confirm
                </label>
                <input
                  id="confirm-typed"
                  value={typed}
                  autoFocus
                  spellCheck={false}
                  autoComplete="off"
                  placeholder={requireTyped}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') go(); }}
                  className="mt-1.5 h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm text-foreground"
                />
              </div>
            ) : null}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => { setOpen(false); settle(false); }}>{cancelLabel}</Button>
              <Button variant={destructive ? 'destructive' : 'default'} disabled={!ready} onClick={go}>
                {confirmLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    }
    root.render(<Wrapper />);
  });
}
